import {
  AccountBalance,
  CalendarToday,
  Cancel,
  CheckCircle,
  ContentCopy,
  EmojiEvents,
  Event,
  Rocket,
  Schedule,
  Star,
  TrendingUp,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  createLink,
  sahinAlertDialog,
} from '@sahinyazilim/sahinyazilim-components';
import { AccountRouteNames } from 'account/common/model/AccountRouteNames';
import { Payment } from 'account/payment/model/payment';
import { RootState } from 'common/context/store';
import { NullUndefined } from 'common/model/NullUndefined';
import { OwnerEnv } from 'common/utils/env';
import { currencyFormat } from 'common/utils/helper';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface PaymentListProps {
  data?: NullUndefined<Payment[]>;
}

export const PaymentList: React.FC<PaymentListProps> = ({ data }) => {
  const currentPlan = useSelector(
    (state: RootState) => state.common.currentPlan,
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle fontSize="small" />;
      case 'PENDING':
        return <Schedule fontSize="small" />;
      case 'CANCELLED':
        return <Cancel fontSize="small" />;
      case 'EXPIRED':
        return <CheckCircle fontSize="small" />;
      default:
        return undefined;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const calculatePlanEndDate = (startDate: string, quantity: string) => {
    const start = new Date(startDate);
    const end = new Date(start);
    end.setMonth(end.getMonth() + Number(quantity));
    return end;
  };

  return (
    <Box sx={{ width: '80%', minWidth: '550px' }}>
      <Typography
        variant="h5"
        sx={{ mt: 4, mb: 1, color: 'text.primary', fontSize: 18 }}
      >
        Ödeme Geçmişi
      </Typography>
      <Box sx={{ maxHeight: '80vh', overflowY: 'auto', pr: 2 }}>
        {data?.length === 0 || data === null ? (
          <FreeCard />
        ) : (
          data?.map((payment) => (
            <Card
              key={payment.id}
              sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider' }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  {/* Sol taraf - Plan bilgileri */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                      >
                        {payment.plan.title} Planı
                      </Typography>
                      {currentPlan?.id === payment.id && (
                        <Tooltip title="Aktif Plan" arrow>
                          <CheckCircle
                            sx={{
                              fontSize: 20,
                              color: 'success.main',
                              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Event
                        fontSize="small"
                        sx={{ color: 'text.secondary' }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {formatShortDate(payment.createdAt)} -{' '}
                        {formatShortDate(
                          calculatePlanEndDate(
                            payment.createdAt,
                            payment.quantity,
                          ).toISOString(),
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Sağ taraf - Ödeme bilgileri */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body1">
                        {currencyFormat(payment.totalPrice)}
                      </Typography>
                      <Tooltip
                        title={
                          payment.status === 'SUCCESS'
                            ? 'Ödeme Başarılı'
                            : payment.status === 'PENDING'
                            ? 'Ödeme Bekleniyor'
                            : payment.status === 'CANCELLED'
                            ? 'Ödeme İptal Edildi'
                            : payment.status === 'EXPIRED'
                            ? 'Plan Süresi Doldu'
                            : `Bilinmeyen Durum: ${payment.status}`
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor:
                              payment.status === 'SUCCESS'
                                ? 'success.light'
                                : payment.status === 'PENDING'
                                ? 'warning.main'
                                : payment.status === 'CANCELLED'
                                ? 'error.main'
                                : payment.status === 'EXPIRED'
                                ? 'success.light'
                                : 'grey.500',
                            color: 'white',
                            cursor: 'pointer',
                          }}
                        >
                          {getStatusIcon(payment.status)}
                        </Box>
                      </Tooltip>
                    </Box>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <CalendarToday
                        fontSize="small"
                        sx={{ color: 'text.secondary' }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {formatDate(payment.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Ödeme bekleniyor durumunda banka bilgileri */}
                {payment.status === 'PENDING' && (
                  <BankInfoCard payment={payment} />
                )}
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    </Box>
  );
};

const BankInfoCard = ({ payment }: { payment: Payment }) => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Alert
      severity="info"
      icon={<AccountBalance />}
      sx={{
        mt: 2,
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Ödeme Bilgileri
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 80 }}>
              Tutar:
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: 'primary.main' }}
            >
              {currencyFormat(payment.totalPrice)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 80 }}>
              Referans:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                backgroundColor: 'background.paper',
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {payment.paymentId || 'N/A'}
            </Typography>
            <Tooltip title="Kopyala">
              <IconButton
                size="small"
                onClick={() => {
                  sahinAlertDialog({
                    message: 'Referans kodunuz kopyalandı',
                    type: 'success',
                  });
                  copyToClipboard(payment.paymentId || '');
                }}
                sx={{ p: 0.5 }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 80 }}>
              IBAN:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                backgroundColor: 'background.paper',
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              {OwnerEnv.bank.iban}
            </Typography>
            <Tooltip title="Kopyala">
              <IconButton
                size="small"
                onClick={() => {
                  sahinAlertDialog({
                    message: 'IBAN kopyalandı',
                    type: 'success',
                  });
                  copyToClipboard(OwnerEnv.bank.iban);
                }}
                sx={{ p: 0.5 }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 80 }}>
              Banka:
            </Typography>
            <Typography variant="body2">{OwnerEnv.bank.name}</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 80 }}>
              Alıcı:
            </Typography>
            <Typography variant="body2">
              {OwnerEnv.bank.accountHolder}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
        >
          💡 Ödeme açıklamasına referans kodunuzu yazmayı unutmayın!
        </Typography>
      </Box>
    </Alert>
  );
};

const FreeCard = () => {
  const navigate = useNavigate();

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none',
        width: '450px',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        },
      }}
    >
      <CardContent
        sx={{
          p: 4,
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.9)',
            mb: 3,
            fontSize: '1.1rem',
            lineHeight: 1.6,
          }}
        >
          Haha! Henüz hiç ödeme yapmamışsınız çünkü ücretsiz plan
          kullanıyorsunuz! 😄 <br /> Ama gerçekten işinizi büyütmek
          istiyorsanız, <strong>premium planlara geçin</strong>💰
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
              px: 2,
              py: 1,
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Star sx={{ fontSize: 20, color: '#FFD700' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Premium Özellikler
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
              px: 2,
              py: 1,
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
            }}
          >
            <TrendingUp sx={{ fontSize: 20, color: '#4CAF50' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              İş Büyütme
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
              px: 2,
              py: 1,
              borderRadius: 2,
              backdropFilter: 'blur(10px)',
            }}
          >
            <EmojiEvents sx={{ fontSize: 20, color: '#FF9800' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Özel Avantajlar
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            animation: 'bounce 2s infinite',
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': {
                transform: 'translateY(0)',
              },
              '40%': {
                transform: 'translateY(-10px)',
              },
              '60%': {
                transform: 'translateY(-5px)',
              },
            },
          }}
        >
          <Rocket
            sx={{
              fontSize: 60,
              color: 'white',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
            }}
          />
        </Box>

        <Button
          variant="contained"
          size="large"
          sx={{
            background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
            color: 'white',
            fontWeight: 700,
            fontSize: '1.1rem',
            px: 4,
            py: 1.5,
            borderRadius: 3,
            textTransform: 'none',
            boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(0,0,0,0.4)',
              background: 'linear-gradient(45deg, #FF5252, #26A69A)',
            },
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
              },
              '50%': {
                boxShadow: '0 8px 25px rgba(255,107,107,0.4)',
              },
              '100%': {
                boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
              },
            },
          }}
          onClick={() => {
            navigate(
              createLink(AccountRouteNames, AccountRouteNames.PLAN_SELECT),
            );
          }}
        >
          🎭 Premium&apos;a Geç!
        </Button>
      </CardContent>
    </Card>
  );
};
